/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable @typescript-eslint/naming-convention */
import { Component, JSXElement, createSignal } from 'solid-js';
import { BtnClasses } from '../../types/common';
import { getSvgIcon, TempSvgIcon } from './svg-icons/get-svg-icon';

export type ButtonType = 'solid' | 'hollow' | 'outline';
export type ButtonSize = 'small' | 'medium' | 'large';
export type IconPosition = 'left' | 'right' | 'dropdown';
export type ButtonState = 'default' | 'active' | 'checked';

export interface ButtonProps {
  // Content
  content?: JSXElement | string;
  children?: JSXElement | string;

  // Button appearance
  type?: ButtonType;
  color?: BtnClasses;
  size?: ButtonSize;
  fullWidth?: boolean;

  // Icon support
  icon?: string;
  iconPosition?: IconPosition;
  iconOnly?: boolean;

  // State
  disabled?: boolean;
  state?: ButtonState;

  // Behavior
  onClick?(): void; // Handler function passed from web component wrapper
  buttonType?: 'button' | 'submit' | 'reset';

  // Accessibility
  title?: string;
  'aria-label'?: string;
  'data-cy'?: string;

  // Additional classes
  class?: string;
  // Analytics
  'data-analytics-id'?: string;
}

const Button: Component<ButtonProps> = (props): JSXElement => {
  const [isHovered, setIsHovered] = createSignal(false);

  // Reactive getters for props
  const getContent = () => props.content || props.children || '';
  const getType = () => props.type || 'solid';
  const getColor = () => props.color || BtnClasses.PRIMARY;
  const getSize = () => props.size || 'medium';
  const getIconPosition = () => props.iconPosition || 'left';
  const getState = () => props.state || 'default';
  const getButtonType = () => props.buttonType || 'button';
  const getDisabled = () => props.disabled || false;
  const getFullWidth = () => props.fullWidth || false;
  const getIconOnly = () => props.iconOnly || false;

  // Icon handling
  const getIcon = () => props.icon || '';
  const hasIcon = () => !!getIcon();
  const shouldShowIcon = () =>
    hasIcon() && (getIconOnly() || getIconPosition() !== 'dropdown');

  // Class name computation
  const getButtonClasses = () => {
    const classes = ['button', '!ml-0'];

    // Base color class
    classes.push(getColor());

    // Button type (solid/hollow/outline)
    if (getType() !== 'solid') {
      classes.push(getType());
    }

    // Size classes
    if (getSize() !== 'medium') {
      if (getIconOnly()) {
        classes.push(`icon-${getSize()}`);
      } else {
        classes.push(getSize());
      }
    }

    // Icon classes
    if (getIconOnly()) {
      classes.push(`icon-${getSize()}`);
    } else if (hasIcon()) {
      classes.push(`icon-${getIconPosition()}`);
    }

    // State classes
    if (getState() === 'active' || isHovered()) {
      classes.push('active');
    }
    if (getState() === 'checked') {
      classes.push('checked');
    }

    // Full width
    if (getFullWidth()) {
      classes.push('full-width');
    }

    // Disabled state
    if (getDisabled()) {
      classes.push('disabled');
    }

    // Additional custom classes
    if (props.class) {
      classes.push(props.class);
    }

    return classes.join(' ');
  };

  const isSvgIcon = (): boolean => getIcon().startsWith('svg-');

  // Icon rendering
  const renderIcon = () => {
    if (!shouldShowIcon()) return null;

    const iconName = getIcon();
    // Use connect icons
    if (iconName.startsWith('tc-') || iconName.includes('tc-')) {
      const iconClasses = ['icon-font'];
      if (getSize() === 'small' && getIconOnly()) {
        iconClasses.push('i16');
      }
      if (getSize() === 'medium' && getIconOnly()) {
        iconClasses.push('!w-6 !h-6');
      }
      if (getSize() === 'large' && getIconOnly()) {
        iconClasses.push('i32');
      }
      iconClasses.push(iconName);
      return <i class={iconClasses.join(' ')} aria-hidden="true" />;
    }

    if (isSvgIcon()) {
      //fix the sizing map but then it should probably work for now
      const iconClasses = ['content-center'];
      if (getSize() === 'small' && getIconOnly()) {
        iconClasses.push('i16');
      }
      if (getSize() === 'medium' && getIconOnly()) {
        iconClasses.push('!w-6 !h-6');
      }
      if (getSize() === 'large' && getIconOnly()) {
        iconClasses.push('i32');
      }
      return (
        <span class={iconClasses.join(' ')}>
          {getSvgIcon(props.icon as TempSvgIcon, props.size || 'medium')}
        </span>
      );
    }

    // Use modus icons
    const sizeMap: Record<ButtonSize, 'xs' | 'md' | 'lg'> = {
      small: 'xs',
      medium: 'md',
      large: 'lg',
    };
    const modusSize = sizeMap[getSize()];

    return (
      <modus-wc-icon
        custom-class="!w-auto !h-auto"
        name={iconName}
        size={modusSize}
      />
    );
  };

  // Dropdown arrow for dropdown buttons
  const renderDropdownArrow = () => {
    if (getIconPosition() !== 'dropdown') return null;
    return <i class="icon-font tc-icon-arrow-drop-down" aria-hidden="true" />;
  };

  // Event handlers
  const handleClick = (event: MouseEvent) => {
    if (getDisabled()) {
      event.preventDefault();
      return;
    }

    props.onClick?.();
  };

  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => setIsHovered(false);

  // Content rendering
  const renderContent = () => {
    if (getIconOnly()) {
      return renderIcon();
    }

    const content = getContent();
    const hasContent =
      typeof content === 'string' ? content.trim().length > 0 : !!content;

    if (!hasContent && !hasIcon()) {
      return null;
    }

    return (
      <>
        {getIconPosition() === 'left' && renderIcon()}
        {hasContent && <span>{content}</span>}
        {getIconPosition() === 'right' && renderIcon()}
        {renderDropdownArrow()}
      </>
    );
  };

  return (
    <button
      data-analytics-id={props['data-analytics-id']}
      class={getButtonClasses()}
      type={getButtonType()}
      disabled={getDisabled()}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      title={props.title}
      aria-label={props['aria-label']}
      data-cy={props['data-cy'] || 'button'}
      data-testid={props['data-cy'] || 'button'}
    >
      {renderContent()}
    </button>
  );
};

export default Button;
